import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CustomButton from "../components/CustomButton";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import {
  getCountries,
  getInstitutes,
  submitExtraUserDetails,
} from "../api/Api";
import { useUser } from "../contexts/UserContextProvider";
import { useNavigate } from "react-router-dom";
import { showToast } from "../utils/toastUtils";
import { UserDetailsSchema } from "../utils/validations";
import styles from "./UserExtraDetails.module.css";
import helloImage from "../assets/hello.png";
import TextAreaInput from "../components/Inputs/TextAreaInput";
import ShortTextInput from "../components/Inputs/ShortTextInput";
import AutoCompleteDropdown from "../components/Inputs/AutoCompleteDropdown";

const experience = [
  { id: 1, duration: "0 years" },
  { id: 2, duration: "1-2 years" },
  { id: 3, duration: "3-5 years" },
  { id: 4, duration: "6-10 years" },
  { id: 5, duration: "11-15 years" },
  { id: 6, duration: "16+ years" },
  { id: 7, duration: "Varies" },
];

function UserExtraDetails() {
  const [institutes, setInstitutes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState("");
  const [customInstitute, setCustomInstitute] = useState(false);
  const { userInfo } = useUser();
  const navigate = useNavigate();

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      username: "",
      country: "",
      jobTitle: "",
      department: "",
      experience: "",
      institute_id: "",
      institute_name: "",
      website: "",
      aboutYou: "",
    },
    validate: (values) => {
      const errors = {};
      const requiredFields = ["username", "department", "institute_id"];

      if (customInstitute) {
        requiredFields.push("institute_name");
        let exists = institutes.some(
          (obj) =>
            obj.name.toLowerCase() === values.institute_name.toLowerCase()
        );

        if (exists) errors["institute_name"] = "Institute Already Exists";
      }

      requiredFields.forEach((field) => {
        if (!values[field]) errors[field] = "Required";
      });

      return errors;
    },
    onSubmit: async (values) => {
      /*Create the object with common fields and conditionally include either
      the custom institute fields or the regular institute field*/
      let obj = {
        username: values.username,
        country_code: values.country.code,
        jobTitle: values.jobTitle,
        department: values.department,
        experience: values.experience,
        website: values.website,
        aboutYou: values.aboutYou,
        ...(customInstitute
          ? { institute_id: null, institute_name: values.institute_name }
          : { institute_id: values.institute_id }),
      };

      // Handle form submission logic here
      const response = await submitExtraUserDetails(obj);
      console.log("API response:", response);
      if (response.status === 200) {
        navigate("/home");
      } else if (response.message === "Username already exists.") {
        showToast("Username already exists.", "warning");
        navigate("/home");
      }
    },
    validationSchema: UserDetailsSchema,
  });

  useEffect(() => {
    // load institutes
    getInstitutes().then((response) => {
      console.log(response);
      if (response.status === 200) {
        setInstitutes([
          { id: -1, name: "Other (Please Enter your institute below)" },
          ...response.data,
        ]);
        return;
      }
      setError(response.message);
    });

    // load countries
    getCountries().then((response) => {
      console.log(response);
      if (response.status === 200) {
        setCountries(response.data);
        return;
      }
      setError(response.message);
    });
  }, []);

  console.log(institutes);
  const handleInstituteDropdownOnSelect = (event, value) => {
    console.log(value);
    if (value && value.id === -1) {
      setCustomInstitute(true);
    } else {
      setCustomInstitute(false);
    }

    formik.setFieldValue("institute_id", value ? value.id : null);
  };

  const fname = userInfo?.name.split(" ")[0];

  if (error)
    return (
      <p className="d-flex justify-content-center m-4 alert alert-danger">
        {error}
      </p>
    );

  return (
    <div
      className={`${styles["user-extra-details"]} container-fluid my-5 px-md-0 px-4`}
      style={{ maxWidth: "400px" }}
    >
      <div className="text-center mb-4">
        <div className="d-flex justify-content-center align-items-center text-center">
          <img
            src={helloImage}
            alt="Hello"
            style={{ width: "30px", height: "30px", marginRight: "10px" }}
          />
          <h2 className="mb-0">Hello {fname},</h2>
        </div>
        <p className="text-color-secondary fs-4">
          Just a few more steps to complete your profile
        </p>
        <i className="text-color-secondary fs-4">
          By filling out this information, you’ll make it easier for others to
          connect with you for collaborations.
        </i>
      </div>

      <form id="user-extra-details-form" onSubmit={formik.handleSubmit}>
        <Row>
          <Col>
            <label htmlFor="username">
              Username
              <span className="text-danger"> *</span>
            </label>
            <ShortTextInput
              id="username"
              placeholder="Enter your username"
              {...formik.getFieldProps("username")}
              error={formik.touched.username && formik.errors.username}
              helperText={formik.touched.username && formik.errors.username}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label htmlFor="country">Country</label>
            <AutoCompleteDropdown
              disablePortal
              options={countries}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={formik.touched.country && formik.errors.country}
                  helperText={formik.touched.country && formik.errors.country}
                />
              )}
              onChange={(event, value) =>
                formik.setFieldValue("country", value)
              }
            />
          </Col>
          <Col>
            <label htmlFor="jobTitle">Job title</label>
            <ShortTextInput
              id="jobTitle"
              placeholder="Enter your job title"
              {...formik.getFieldProps("jobTitle")}
              error={formik.touched.jobTitle && formik.errors.jobTitle}
              helperText={formik.touched.jobTitle && formik.errors.jobTitle}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label htmlFor="department">
              Department/ Research group
              <span className="text-danger"> *</span>
            </label>
            <ShortTextInput
              id="department"
              placeholder="Type your Department/ Research group name"
              {...formik.getFieldProps("department")}
              error={formik.touched.department && formik.errors.department}
              helperText={formik.touched.department && formik.errors.department}
            />
          </Col>
          <Col>
            <label htmlFor="experience">
              How long have you held current position?
            </label>
            <AutoCompleteDropdown
              disablePortal
              options={experience.map((item) => ({ value: item.duration }))}
              getOptionLabel={(option) => option.duration} // Replace with actual experience options
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={formik.touched.experience && formik.errors.experience}
                  helperText={
                    formik.touched.experience && formik.errors.experience
                  }
                />
              )}
              onChange={(event, value) =>
                formik.setFieldValue("experience", value)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label htmlFor="institute_id">
              Institute
              <span className="text-danger"> *</span>
            </label>
            <AutoCompleteDropdown
              disablePortal
              id="institute-autocomplete"
              options={institutes || []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              filterOptions={(options, state) => {
                const inputValue = state.inputValue.trim().toLowerCase();
                
                // If empty input, show all options (limited to first 30 for performance)
                if (inputValue.length === 0) {
                  // Find "Other" option to prioritize
                  const otherOption = options.find(opt => opt.id === -1);
                  
                  // Get remaining options (excluding "Other")
                  const remainingOptions = options.filter(opt => opt.id !== -1).slice(0, 29);
                  
                  // Return "Other" first, followed by regular options
                  return otherOption ? [otherOption, ...remainingOptions] : remainingOptions;
                }
                
                // Otherwise filter based on input and take first 30 matches
                return options
                  .filter((option) => option.name.toLowerCase().includes(inputValue))
                  .slice(0, 30);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Institute"
                  error={
                    formik.touched.institute_id &&
                    Boolean(formik.errors.institute_id)
                  }
                  helperText={
                    formik.touched.institute_id && formik.errors.institute_id
                  }
                />
              )}
              value={
                institutes?.find(
                  (inst) => inst.id === formik.values.institute_id
                ) || null
              }
              onChange={handleInstituteDropdownOnSelect}
                  error={formik.touched.institute_id && formik.errors.institute_id}
            />
          </Col>
        </Row>
        <Row className={`${customInstitute ? "" : "d-none"}`}>
          <Col>
            <label htmlFor="institute_name">
              institute name
              <span className="text-danger"> *</span>
            </label>
            <ShortTextInput
              id="institute_name"
              placeholder="Enter your institute name"
              {...formik.getFieldProps("institute_name")}
              error={
                formik.touched.institute_name &&
                Boolean(formik.errors.institute_name)
              }
              helperText={
                formik.touched.institute_name && formik.errors.institute_name
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label htmlFor="website">Website / linkedin</label>
            <ShortTextInput
              id="website"
              placeholder="Enter your website"
              {...formik.getFieldProps("website")}
              error={formik.touched.website && formik.errors.website}
              helperText={formik.touched.website && formik.errors.website}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <label htmlFor="aboutYou">About you</label>
            <TextAreaInput
              id="aboutYou"
              placeholder="Type here"
              {...formik.getFieldProps("aboutYou")}
              error={formik.touched.aboutYou && formik.errors.aboutYou}
              helperText={formik.touched.aboutYou && formik.errors.aboutYou}
            />
          </Col>
          <CustomButton className="w-100 mt-4" type="submit">
            Next
          </CustomButton>
        </Row>
      </form>
    </div>
  );
}

export default UserExtraDetails;
